import { useCallback, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';
import { KnowledgeBase, Users } from '@mint-sdk/api-ai-assistant';

import type { FolderAccessUser } from '../../helpers/knowledge-base/userName.js';

type Options = {
  granted?: KnowledgeBase.Folder['grantedUsers'];
  owner?: KnowledgeBase.Folder['owner'];
};

export type FolderAccessUsers = ReturnType<typeof useFolderAccessUsers>;

export const useFolderAccessUsers = ({ granted, owner }: Options = {}) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { profileService } = useProfile();
  const profile = profileService.getProfileData()?.profile;

  const { data: meData } = Users.getMe.use({});
  const { data: workspaceUsers = [], isLoading: isLoadingUsers } =
    KnowledgeBase.listWorkspaceUsers.use({});

  const me = useMemo(
    () => ({
      id: meData && String(meData.userId),
      firstName: profile?.firstName,
      lastName: profile?.lastName,
    }),
    [meData, profile?.firstName, profile?.lastName],
  );

  const ownerId = owner?.userId == null ? undefined : String(owner.userId);

  // Workspace users win, as only they carry an email; grantees and the owner
  // who left the workspace still resolve from the folder.
  const users = useMemo(
    () =>
      new Map<string, FolderAccessUser>([
        ...(granted ?? []).flatMap((user) =>
          user.userId == null ? [] : [[String(user.userId), user] as const],
        ),
        ...(owner && ownerId ? [[ownerId, owner] as const] : []),
        ...workspaceUsers.map((user) => [String(user.userId), user] as const),
      ]),
    [granted, owner, ownerId, workspaceUsers],
  );

  const resolveUser = useCallback((id: string) => users.get(id), [users]);

  return {
    me,
    ownerId,
    workspaceName: profile?.workspace?.name ?? t('Workspace'),
    workspaceUsers,
    isLoadingUsers,
    resolveUser,
  };
};
